import { supabase, type User, type UserRole } from './supabase'

export interface AuthUser extends User {
  role: UserRole
}

/**
 * Sign in with email and password
 * Returns the full AuthUser (with DB role) so callers don't need to call getCurrentUser() again.
 */
export async function signIn(email: string, password: string): Promise<AuthUser> {
  // Helper to sign out without hanging — fire and forget with a short timeout
  const safeSignOut = () => {
    try { supabase.auth.signOut().catch(() => {}) } catch {}
  }

  // signInWithPassword needs a timeout — if Supabase auth is slow, the form hangs forever
  let data: any
  try {
    const result = await Promise.race([
      supabase.auth.signInWithPassword({ email, password }),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('auth_timeout')), 10000)
      ),
    ])
    if (result.error) throw result.error
    data = result.data
  } catch (e: any) {
    if (e?.message === 'auth_timeout') {
      throw new Error('La connexion a pris trop de temps. Veuillez réessayer.')
    }
    throw e
  }

  // Fetch role from DB with a timeout to prevent hanging if DB is slow
  let userData: any = null
  try {
    const result = await Promise.race([
      supabase.from('users').select('*').eq('id', data.user.id).single(),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('db_timeout')), 5000)
      ),
    ])
    userData = result.data ?? null
    if (result.error) throw result.error
  } catch (e: any) {
    safeSignOut()
    if (e?.message === 'db_timeout') {
      throw new Error('La connexion a pris trop de temps. Veuillez réessayer.')
    }
    throw new Error('Impossible de récupérer votre profil.')
  }

  if (!userData) {
    safeSignOut()
    throw new Error('Impossible de récupérer votre profil.')
  }

  // Check if user has access to back office (must be doctor or admin)
  if (!hasBackOfficeAccess(userData.user_role)) {
    // Un médecin inscrit garde le rôle `patient` jusqu'à son approbation, qui
    // lui donne son rôle (migration 127). Il lisait « Accès refusé, seuls les
    // médecins… » — sans un mot sur son dossier d'inscription. Sa propre fiche
    // lui est lisible (« Doctors can view own profile ») : on dit où il en est.
    const { data: fiche } = await supabase
      .from('doctors')
      .select('verification_status')
      .eq('user_id', data.user.id)
      .maybeSingle()
    safeSignOut()
    if (fiche?.verification_status === 'pending') {
      throw new Error(
        'Votre inscription de médecin est en cours de validation par l’équipe B-SohTak. Vous pourrez vous connecter dès qu’elle sera approuvée.'
      )
    }
    if (fiche?.verification_status === 'rejected' || fiche?.verification_status === 'suspended') {
      throw new Error(
        'Votre compte médecin n’est pas actif. Contactez l’équipe B-SohTak pour en savoir plus.'
      )
    }
    throw new Error(
      'Accès refusé. Seuls les médecins et administrateurs peuvent accéder au back office.'
    )
  }

  // Update last_login_at (fire and forget)
  void supabase
    .from('users')
    .update({ last_login_at: new Date().toISOString() })
    .eq('id', data.user.id)

  return { ...userData, role: userData.user_role }
}

/**
 * Sign out
 */
export async function signOut() {
  const { error } = await supabase.auth.signOut()
  if (error) {
    throw error
  }
}

/**
 * Get current authenticated user with role
 */
export async function getCurrentUser(): Promise<AuthUser | null> {
  try {
    // Timeout on getUser() — if Supabase doesn't respond, bail out cleanly.
    // IMPORTANT: Never call signOut() here. A timeout is NOT a corrupted session.
    // Calling signOut() on transient errors would destroy a valid session on slow networks.
    let authUser: any = null
    try {
      const result = await Promise.race([
        supabase.auth.getUser(),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('auth_timeout')), 8000)
        ),
      ])
      if (result.error || !result.data?.user) return null
      authUser = result.data.user
    } catch {
      // Timeout or network error — just return null, do NOT destroy the session
      return null
    }

    let userData: any = null
    try {
      const result = await Promise.race([
        supabase.from('users').select('*').eq('id', authUser.id).single(),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('timeout')), 5000)
        ),
      ])
      userData = result.data ?? null
    } catch {
      return null
    }

    if (!userData) {
      return null
    }

    return {
      ...userData,
      role: userData.user_role,
    }
  } catch {
    return null
  }
}

/**
 * Check if user has access to back office
 * Only doctors and admins can access
 */
export function hasBackOfficeAccess(role: UserRole): boolean {
  return ['doctor', 'bsohtak_doctor', 'admin'].includes(role)
}

/**
 * Check if current user is admin
 */
export function isAdmin(role: UserRole): boolean {
  return role === 'admin'
}

/**
 * Check if current user is a doctor (any type)
 */
export function isDoctor(role: UserRole): boolean {
  return role === 'doctor' || role === 'bsohtak_doctor'
}

/**
 * Check if current user is a B'Sohtak doctor
 */
export function isBSohtakDoctor(role: UserRole): boolean {
  return role === 'bsohtak_doctor'
}

/**
 * Reset password request
 */
export async function requestPasswordReset(email: string) {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`,
  })

  if (error) {
    throw error
  }
}

/**
 * Update password
 */
export async function updatePassword(newPassword: string) {
  const { error } = await supabase.auth.updateUser({
    password: newPassword,
  })

  if (error) {
    throw error
  }
}

/**
 * Subscribe to auth state changes
 */
export function onAuthStateChange(
  callback: (user: AuthUser | null, event: string) => void
) {
  return supabase.auth.onAuthStateChange((event, session) => {
    // Don't call getCurrentUser() for PASSWORD_RECOVERY — it can interfere with
    // the recovery session before ResetPasswordForm has a chance to process it.
    if (event === 'PASSWORD_RECOVERY') {
      callback(null, event)
      return
    }

    if (!session?.user) {
      callback(null, event)
      return
    }

    // Jamais d'appel Supabase ATTENDU dans ce rappel : supabase-js l'exécute
    // sous son verrou d'authentification, et getCurrentUser() en redemande
    // un — interblocage jusqu'à expiration. Vu en prod le 30/10 sur la
    // réinitialisation du mot de passe : updateUser déclenche USER_UPDATED,
    // la page restait figée plus de 10 s. On sort du rappel d'abord.
    setTimeout(() => {
      getCurrentUser()
        .then((user) => callback(user, event))
        .catch(() => callback(null, event))
    }, 0)
  })
}
